import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';

export interface SharedDashboardWidget {
  id: string;
  dashboardName: string;
  widgetConfig: string;
  sharedByUserId: string;
  sharedAt: string;
  title: string;
  isActive: boolean;
}

export interface ShareWidgetInput {
  dashboardName: string;
  widgetConfig: string;
  title: string;
}

const BASE_PATH = '/api/core/dashboard-preferences/shared-widgets';

export const sharedWidgetKeys = {
  all: ['sharedWidgets'] as const,
  byDashboard: (name: string) => [...sharedWidgetKeys.all, name] as const,
};

export function useSharedDashboardWidgets(dashboardName: string) {
  return useQuery({
    queryKey: sharedWidgetKeys.byDashboard(dashboardName),
    queryFn: async () => {
      const { data } = await apiClient.get<SharedDashboardWidget[]>(
        `${BASE_PATH}/${dashboardName}`,
      );
      return data;
    },
  });
}

export function useShareWidget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ShareWidgetInput) => {
      const { data } = await apiClient.post<SharedDashboardWidget>(BASE_PATH, input);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: sharedWidgetKeys.byDashboard(data.dashboardName),
      });
    },
  });
}

export function useDeactivateSharedWidget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.post(`${BASE_PATH}/${id}/deactivate`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sharedWidgetKeys.all });
    },
  });
}
